import { supabase } from "@/lib/supabase/client";
import type { Database } from "@/lib/supabase/types";
import type { EventRepository, CreateEventInput, UpdateEventInput } from "@/data/repositories/eventRepository";
import type { Event } from "@/types/domain/event";

type EventRow = Database["public"]["Tables"]["events"]["Row"] & {
  event_types: { key: string } | null;
  wedding_details: Database["public"]["Tables"]["wedding_details"]["Row"] | null;
};
const EVENT_SELECT = "*, event_types(key), wedding_details(*)";

function rowToEvent(row: EventRow): Event {
  return {
    id: row.id,
    organizationId: row.organization_id,
    eventTypeId: row.event_type_id,
    eventTypeKey: row.event_types?.key,
    title: row.title,
    status: row.status as Event["status"],
    lifecycleStage: row.lifecycle_stage as Event["lifecycleStage"],
    startDate: row.start_date,
    endDate: row.end_date ?? undefined,
    timezone: row.timezone,
    venueName: row.venue_name ?? undefined,
    venueCity: row.venue_city ?? undefined,
    guestCountEstimate: row.guest_count_estimate ?? undefined,
    budgetEstimate: row.budget_estimate ?? undefined,
    currency: row.currency,
    coverImageUrl: row.cover_image_url ?? undefined,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    suspendedAt: row.suspended_at ?? undefined,
    deletedAt: row.deleted_at ?? undefined,
    purgeAfter: row.purge_after ?? undefined,
    weddingDetails: row.wedding_details ? {
      eventId: row.wedding_details.event_id,
      partnerOneName: row.wedding_details.partner_one_name,
      partnerTwoName: row.wedding_details.partner_two_name,
      partnerOneEmail: row.wedding_details.partner_one_email ?? undefined,
      partnerOnePhone: row.wedding_details.partner_one_phone ?? undefined,
      partnerTwoEmail: row.wedding_details.partner_two_email ?? undefined,
      partnerTwoPhone: row.wedding_details.partner_two_phone ?? undefined,
      ceremonyType: row.wedding_details.ceremony_type ?? undefined,
      ceremonyStartTime: row.wedding_details.ceremony_start_time ?? undefined,
      receptionStartTime: row.wedding_details.reception_start_time ?? undefined,
      weddingStyle: row.wedding_details.wedding_style ?? undefined,
      weddingColors: row.wedding_details.wedding_colors ?? undefined,
      createdAt: row.wedding_details.created_at,
      updatedAt: row.wedding_details.updated_at,
    } : undefined,
  };
}

function weddingDetailsToRow(eventId: string, details: NonNullable<CreateEventInput["weddingDetails"]>): Database["public"]["Tables"]["wedding_details"]["Insert"] {
  return {
    event_id: eventId,
    ...(details.partnerOneName !== undefined && { partner_one_name: details.partnerOneName }),
    ...(details.partnerTwoName !== undefined && { partner_two_name: details.partnerTwoName }),
    ...(details.partnerOneEmail !== undefined && { partner_one_email: details.partnerOneEmail || null }),
    ...(details.partnerOnePhone !== undefined && { partner_one_phone: details.partnerOnePhone || null }),
    ...(details.partnerTwoEmail !== undefined && { partner_two_email: details.partnerTwoEmail || null }),
    ...(details.partnerTwoPhone !== undefined && { partner_two_phone: details.partnerTwoPhone || null }),
  };
}

function createInputToRow(input: CreateEventInput): Database["public"]["Tables"]["events"]["Insert"] {
  return {
    organization_id: input.organizationId,
    event_type_id: input.eventTypeId,
    title: input.title,
    status: input.status,
    lifecycle_stage: input.lifecycleStage,
    start_date: input.startDate,
    timezone: input.timezone,
    venue_name: input.venueName,
    venue_city: input.venueCity,
    guest_count_estimate: input.guestCountEstimate,
    budget_estimate: input.budgetEstimate,
    currency: input.currency,
    cover_image_url: input.coverImageUrl,
    created_by: input.createdBy,
  };
}

function updateInputToRow(patch: UpdateEventInput): Database["public"]["Tables"]["events"]["Update"] {
  return {
    ...(patch.title !== undefined && { title: patch.title }),
    ...(patch.status !== undefined && { status: patch.status }),
    ...(patch.lifecycleStage !== undefined && { lifecycle_stage: patch.lifecycleStage }),
    ...(patch.startDate !== undefined && { start_date: patch.startDate }),
    ...(patch.venueName !== undefined && { venue_name: patch.venueName }),
    ...(patch.venueCity !== undefined && { venue_city: patch.venueCity }),
    ...(patch.guestCountEstimate !== undefined && { guest_count_estimate: patch.guestCountEstimate }),
    ...(patch.budgetEstimate !== undefined && { budget_estimate: patch.budgetEstimate }),
    ...(patch.coverImageUrl !== undefined && { cover_image_url: patch.coverImageUrl }),
    ...(patch.suspendedAt !== undefined && { suspended_at: patch.suspendedAt }),
    ...(patch.deletedAt !== undefined && { deleted_at: patch.deletedAt }),
    ...(patch.purgeAfter !== undefined && { purge_after: patch.purgeAfter }),
  };
}

/**
 * Real implementation, wired against the `events` table from
 * supabase/migrations/0001_core_platform.sql. Not selected by default
 * anywhere (see src/data/repositories/index.ts) until that migration has
 * actually been applied to a live project — dummy mode is what runs today.
 */
export class SupabaseEventRepository implements EventRepository {
  async listByOrganization(organizationId: string): Promise<Event[]> {
    const { data, error } = await supabase
      .from("events")
      .select(EVENT_SELECT)
      .eq("organization_id", organizationId)
      .order("start_date", { ascending: true });
    if (error) throw error;
    return (data ?? []).map(rowToEvent);
  }

  async getById(id: string): Promise<Event | null> {
    const { data, error } = await supabase.from("events").select(EVENT_SELECT).eq("id", id).maybeSingle();
    if (error) throw error;
    return data ? rowToEvent(data) : null;
  }

  async create(input: CreateEventInput): Promise<Event> {
    let eventTypeId = input.eventTypeId;
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(eventTypeId)) {
      const { data: eventType, error: eventTypeError } = await supabase.from("event_types").select("id").eq("key", "wedding").eq("is_active", true).single();
      if (eventTypeError) throw eventTypeError;
      eventTypeId = eventType.id;
    }
    const { data, error } = await supabase.from("events").insert(createInputToRow({ ...input, eventTypeId })).select(EVENT_SELECT).single();
    if (error) throw error;
    if (input.weddingDetails) {
      const { error: detailsError } = await supabase.from("wedding_details").upsert(weddingDetailsToRow(data.id, input.weddingDetails), { onConflict: "event_id" });
      if (detailsError) throw detailsError;
      return (await this.getById(data.id)) ?? rowToEvent(data);
    }
    return rowToEvent(data);
  }

  async update(id: string, patch: UpdateEventInput): Promise<Event> {
    const { weddingDetails, ...eventPatch } = patch;
    const { data, error } = await supabase
      .from("events")
      .update(updateInputToRow(eventPatch))
      .eq("id", id)
      .select(EVENT_SELECT)
      .single();
    if (error) throw error;
    if (weddingDetails) {
      const { error: detailsError } = await supabase.from("wedding_details").upsert(weddingDetailsToRow(id, weddingDetails), { onConflict: "event_id" });
      if (detailsError) throw detailsError;
      return (await this.getById(id)) ?? rowToEvent(data);
    }
    return rowToEvent(data);
  }
}
